// @shared:standard — used in DocCommentForm and task comment editors
import { useMemo, useRef } from 'react';
import Mention from '@tiptap/extension-mention';
import { searchUsers } from '../../../utils/decoupled';

const MAX_RESULTS = 8;

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Creates a configured TipTap Mention extension with async user search.
 *
 * Memoized to return the same extension instance across renders so that
 * useEditor doesn't recreate the ProseMirror instance.
 *
 * The extension renders:
 *   <span data-type="mention" data-id="{uid}">@name</span>
 * which is parsed by NotificationManager::extractMentionedUids().
 */
export function useMentionExtension() {
  const cacheRef = useRef({});

  return useMemo(() => {
    async function fetchUsers(query) {
      const key = (query || '').toLowerCase().trim();

      if (cacheRef.current[key]) {
        return cacheRef.current[key];
      }

      try {
        const items = (await searchUsers(key, MAX_RESULTS)).map((u) => ({ id: u.uid, label: u.name }));
        cacheRef.current[key] = items;
        return items;
      } catch {
        return [];
      }
    }

    return Mention.configure({
      HTMLAttributes: {
        class: 'mention',
      },
      renderText({ node }) {
        return `@${node.attrs.label ?? node.attrs.id}`;
      },
      suggestion: {
        char: '@',
        allowSpaces: false,
        items: ({ query }) => fetchUsers(query),
        render: () => {
          let popup = null;
          let currentProps = null;
          let activeIndex = 0;

          function destroy() {
            if (popup) {
              popup.remove();
              popup = null;
            }
            currentProps = null;
            activeIndex = 0;
          }

          function createPopup() {
            popup = document.createElement('div');
            popup.className = 'mention-popup';
            popup.style.position = 'fixed';
            popup.style.zIndex = '10000';
            document.body.appendChild(popup);
          }

          function renderItems() {
            if (!popup || !currentProps) return;

            const { items, clientRect } = currentProps;

            if (clientRect) {
              const rect = typeof clientRect === 'function' ? clientRect() : clientRect;
              if (rect) {
                popup.style.left = `${rect.left}px`;
                popup.style.top = `${rect.bottom + 4}px`;
              }
            }

            if (!items || items.length === 0) {
              popup.innerHTML = `
                <div class="bg-white border border-stone-200 rounded-lg shadow-lg py-2 px-3 text-xs text-stone-400">
                  Aucun utilisateur trouvé
                </div>`;
              return;
            }

            popup.innerHTML = `
              <div class="bg-white border border-stone-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto min-w-[180px]">
                ${items.map((item, i) => `
                  <div
                    data-mention-item="${i}"
                    class="px-3 py-1.5 text-sm cursor-pointer transition-colors ${
                      i === activeIndex
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-stone-700 hover:bg-stone-50'
                    }"
                  >
                    <span class="text-blue-500 mr-0.5">@</span>${escapeHtml(item.label)}
                  </div>
                `).join('')}
              </div>`;

            popup.querySelectorAll('[data-mention-item]').forEach((el) => {
              el.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const idx = parseInt(el.dataset.mentionItem, 10);
                const item = items[idx];
                if (item && currentProps?.command) {
                  currentProps.command(item);
                }
              });
            });
          }

          return {
            onStart(props) {
              currentProps = props;
              activeIndex = 0;
              createPopup();
              renderItems();
            },

            onUpdate(props) {
              currentProps = props;
              activeIndex = 0;
              renderItems();
            },

            onKeyDown({ event }) {
              if (!popup || !currentProps) return false;

              const items = currentProps.items || [];

              if (event.key === 'Escape') {
                destroy();
                return true;
              }

              if (event.key === 'ArrowDown') {
                if (items.length === 0) return true;
                event.preventDefault();
                activeIndex = Math.min(activeIndex + 1, items.length - 1);
                renderItems();
                return true;
              }

              if (event.key === 'ArrowUp') {
                if (items.length === 0) return true;
                event.preventDefault();
                activeIndex = Math.max(activeIndex - 1, 0);
                renderItems();
                return true;
              }

              if (event.key === 'Enter' || event.key === 'Tab') {
                if (items.length > 0 && items[activeIndex]) {
                  event.preventDefault();
                  currentProps.command(items[activeIndex]);
                  return true;
                }
                return false;
              }

              return false;
            },

            onExit() {
              destroy();
            },
          };
        },
      },
    });
  }, []); // Stable — never recreated
}

export default useMentionExtension;
